import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../utils/api";
import "./HeathrowLiveStatusCompact.css";

const RICH_STATUS_URL = "/api/open/heathrow/status?period=today&basis=today";
const LEGOLAND_LIVE_URL = "/api/open/legoland/live-summary";
const ASCOT_RAIL_TARGET_URLS = [
  "/api/rail-corridors/targets/ascot_board/latest?airport_code=LHR&limit=3",
  "/api/rail-corridors/targets/slo_board/latest?airport_code=LHR&airport_relevant=true&limit=3",
];

const domainOrder = [
  ["flights", "Flights", "F"],
  ["terminals", "Terminals", "T"],
  ["queues", "Queues", "Q"],
  ["road", "Roads", "M"],
  ["rail", "Rail & TfL", "R"],
  ["weather", "Weather", "W"],
];

const fallbackSummary = {
  status: {
    overall: {
      status: "unknown",
      label: "Checking",
      score: 0,
      reasons: ["Live airport status is updating."],
    },
    domains: {},
  },
  freshness: {},
  analytics: { reports: {} },
};

function norm(value) {
  return String(value || "").toLowerCase();
}

function statusLabel(value) {
  const status = norm(value);
  if (status === "critical_red") return "Critical";
  if (status === "red") return "High";
  if (status === "amber") return "Moderate";
  if (status === "light_green") return "Watch";
  if (status === "green") return "Good";
  if (/(critical|severe|high|red)/.test(status)) return "High";
  if (/(moderate|busy|amber|watch|delay)/.test(status)) return "Moderate";
  if (/(good|green|clear|normal|on time)/.test(status)) return "Good";
  return "Checking";
}

function toneKey(value) {
  const status = norm(value);
  if (status === "critical_red" || status === "red" || /(critical|severe|high|red|poor)/.test(status)) return "red";
  if (status === "amber" || /(moderate|busy|amber|watch|delay)/.test(status)) return "amber";
  if (status === "light_green" || status === "green" || /(good|green|clear|normal|on time)/.test(status)) return "green";
  return "checking";
}

function cleanText(value, fallback = "Live status updating", max = 98) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  return text.length > max ? `${text.slice(0, max - 3).trim()}...` : text;
}

function formatTemp(value) {
  const temp = Number(value);
  if (!Number.isFinite(temp)) return "";
  return `${Math.round(temp)}C`;
}

function formatAge(value) {
  if (!value) return "Auto-updating";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Auto-updating";
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 90) return "Just updated";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min old`;
  return `${Math.round(seconds / 3600)} hr old`;
}

function formatClock(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  }).format(date);
}

function formatDuration(value) {
  const mins = Number(value);
  if (!Number.isFinite(mins) || mins <= 0) return "";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (!h) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function formatSignedMinutesFromSeconds(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return "";
  const mins = Math.round(seconds / 60);
  if (mins === 0) return "on time";
  return `${mins > 0 ? "+" : ""}${mins}m`;
}

function addMinutes(value, mins) {
  const base = value ? new Date(value) : new Date();
  if (Number.isNaN(base.getTime()) || !Number.isFinite(Number(mins))) return "";
  return new Date(base.getTime() + Number(mins) * 60000).toISOString();
}

function safeList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function rankStatus(value) {
  const tone = toneKey(value);
  if (tone === "red") return 4;
  if (tone === "amber") return 3;
  if (tone === "green") return 2;
  return 1;
}

function terminalStatus(preview = {}) {
  const items = safeList(preview.terminal_pressure);
  const strongest = items
    .slice()
    .sort((a, b) => rankStatus(b?.label || b?.level) - rankStatus(a?.label || a?.level))[0];
  if (strongest) {
    return {
      status: strongest.label || strongest.level || "Checking",
      headline: `${strongest.terminal || "Terminal"} pressure`,
    };
  }
  return { status: "unknown", headline: preview.terminal || "Terminal checks updating" };
}

function queueStatus(preview = {}) {
  const items = safeList(preview.terminal_queues);
  const strongest = items
    .slice()
    .sort((a, b) => rankStatus(b?.label || b?.level) - rankStatus(a?.label || a?.level))[0];
  if (strongest) {
    return {
      status: strongest.label || strongest.level || "Checking",
      headline: `${strongest.terminal || "Queue"} waits updating`,
    };
  }
  return {
    status: "unknown",
    headline: safeList(preview.queues)[0] || "Security and border queues updating",
  };
}

function summaryToRichShape(summaryData, airportCode = "LHR", airportName = "Heathrow") {
  const airport = safeList(summaryData?.airports).find((item) => String(item?.code || "").toUpperCase() === airportCode);
  if (!airport) return fallbackSummary;

  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  const terminals = terminalStatus(preview);
  const queues = queueStatus(preview);

  return {
    status: {
      overall: {
        status: airport.overall?.level || airport.overall?.label || "unknown",
        label: airport.overall?.label || "Checking",
        score: 0,
        reasons: [airport.summary?.headline || airport.overall?.reason || `Live ${airportName} sources are checking.`],
      },
      domains: {
        road: { status: hero.road?.label || "unknown", headline: hero.road?.headline || hero.road?.reason || "Road checks updating", score: 0 },
        rail: { status: hero.rail?.label || "unknown", headline: hero.rail?.headline || hero.rail?.reason || "Rail checks updating", score: 0 },
        flights: { status: hero.flights?.label || "unknown", headline: hero.flights?.headline || hero.flights?.reason || "Flight checks updating", score: 0 },
        terminals: { status: terminals.status, headline: terminals.headline, score: 0 },
        queues: { status: queues.status, headline: queues.headline, score: 0 },
        weather: { status: hero.weather?.label || "unknown", headline: preview.weather || hero.weather?.headline || hero.weather?.reason || "Weather checks updating", score: 0 },
      },
    },
    freshness: {
      status_generated_at: summaryData?.generated_at || airport.updated_at || "",
    },
    analytics: { reports: {} },
  };
}

function buildCards(data, { includeQueues = true } = {}) {
  const domains = data?.status?.domains || {};
  const weatherNow = data?.analytics?.reports?.weather?.overview?.latest || {};
  const weatherText = cleanText(
    [formatTemp(weatherNow.temp_c), weatherNow.weather_text || weatherNow.condition || ""].filter(Boolean).join(" · "),
    "",
    48
  );

  return domainOrder.filter(([key]) => includeQueues || key !== "queues").map(([key, label, icon]) => {
    const domain = domains[key] || {};
    const contributors = safeList(domain.top_contributors).slice(0, 2);
    const headline = key === "weather" && weatherText
      ? weatherText
      : domain.headline || `${label} checking`;
    return {
      key,
      label,
      icon,
      status: domain.status || "unknown",
      headline: cleanText(headline, `${label} checking`, 84),
      score: Number(domain.score || 0),
      contributors,
    };
  });
}

function roadLabel(row = {}) {
  const key = String(row.corridor_key || "").toLowerCase();
  const name = String(row.display_name || "").toLowerCase();
  if (key.includes("m4_j13") || name.includes("reading")) return "Reading to LHR";
  if (key.includes("m4_lhr3") || name.includes("london")) return "London to LHR";
  if (key.includes("m40") || name.includes("oxford")) return "Oxford to LHR";
  if (key.includes("gatwick")) return "Gatwick to LHR";
  if (key.includes("stansted")) return "Stansted to LHR";
  return row.display_name || "Road corridor";
}

function railLabel(row = {}) {
  const key = String(row.target?.target_key || row.target_key || row.corridor_key || row.display_name || "").toLowerCase();
  const family = String(row.selected_journey?.operator_family || row.target?.metadata?.preferred_family || row.metadata?.preferred_family || "").toLowerCase();
  const label = String(row.display_name || row.target?.display_name || "");
  if (key.includes("piccadilly") || family.includes("piccadilly") || label.toLowerCase().includes("underground")) return "Piccadilly line";
  if (family.includes("heathrow") || label.toLowerCase().includes("heathrow express")) return "Heathrow Express";
  if (key.includes("elizabeth") || family.includes("elizabeth") || label.toLowerCase().includes("elizabeth")) return "Elizabeth line";
  return label || "Rail to LHR";
}

function buildAscotRoadImpact(legolandData) {
  const corridors = safeList(legolandData?.road?.corridors);
  if (!corridors.length) return [];

  const wanted = [
    {
      test: (key) => key.includes("lhr3_legoland_m4") || (key.includes("legoland") && key.includes("m4")),
      label: "Heathrow to Ascot",
    },
    {
      test: (key) => key.includes("legoland_fleet_m3") || (key.includes("legoland") && key.includes("m3")),
      label: "M3 to Ascot",
    },
  ];

  return wanted
    .map((item) => {
      const row = corridors.find((corridor) => item.test(String(corridor?.corridor_key || "").toLowerCase()));
      if (!row) return null;
      const duration = Math.round(Number(row.route_duration_live_sec || 0) / 60);
      const delta = formatSignedMinutesFromSeconds(row.route_delta_sec);
      return {
        key: `ascot-road-${row.corridor_key}`,
        label: item.label,
        status: row.status_label || "unknown",
        detail: [formatDuration(duration), delta].filter(Boolean).join(", "),
        eta: formatClock(addMinutes(row.observed_at_utc, duration)),
        destinationLabel: "Ascot",
      };
    })
    .filter(Boolean);
}

function boardSummary(board = {}) {
  const cancelled = Number(board.cancelled_count || 0);
  const delayed = Number(board.delayed_count || 0);
  const services = Number(board.service_count || 0);
  if (cancelled || delayed) {
    return `${cancelled} cancelled, ${delayed} delayed`;
  }
  if (services > 0) return `${services} live services checked`;
  return "Live board updating";
}

function buildAscotRailImpact(railTargets = []) {
  const wanted = [
    { key: "ascot_board", label: "Ascot station" },
    { key: "slo_board", label: "Slough rail" },
  ];

  return wanted.map((item) => {
    const row = safeList(railTargets).find((target) => String(target?.target?.target_key || "").toLowerCase() === item.key);
    const board = row?.board || null;
    const observed = board?.observed_at_utc || row?.target?.effective_observed_at_utc || "";
    return {
      key: `ascot-rail-${item.key}`,
      label: item.label,
      status: board?.status_label || row?.target?.effective_status_label || "unknown",
      detail: board ? boardSummary(board) : "Live board updating",
      eta: observed ? formatClock(observed) : "",
      timePrefix: observed ? "Updated" : "",
    };
  });
}

function buildJourneyImpact(data, { statusProfile = "", attractionData = null, railTargetData = [] } = {}) {
  const reports = data?.analytics?.reports || {};
  const isAscot = String(statusProfile || "").toLowerCase() === "ascot";
  const ascotRoads = isAscot ? buildAscotRoadImpact(attractionData) : [];
  const ascotRails = isAscot ? buildAscotRailImpact(railTargetData) : [];
  const roads = safeList(reports?.road?.overview?.rankings?.corridors)
    .filter((row) => {
      const key = String(row.corridor_key || "").toLowerCase();
      const name = String(row.display_name || "").toLowerCase();
      return (key.includes("lhr") || name.includes("heathrow")) && !key.startsWith("lhr3_");
    })
    .slice(0, 2)
    .map((row) => ({
      key: `road-${row.corridor_key}`,
      label: roadLabel(row),
      status: row.status_label,
      detail: `${formatDuration(row.live_duration_mins)}${Number.isFinite(Number(row.delay_mins)) ? `, ${Number(row.delay_mins) > 0 ? "+" : ""}${Math.round(Number(row.delay_mins))}m` : ""}`,
      eta: formatClock(addMinutes(row.observed_at_utc, row.live_duration_mins)),
    }));

  const railRows = [
    ...safeList(data?.journey_impact?.tfl_journey_paths || reports?.rail?.facts?.facts?.tfl_journeys),
    ...safeList(reports?.rail?.overview?.corridors),
  ]
    .filter((row) => String(row.display_name || row.target?.display_name || row.target_key || row.target?.target_key || "").toLowerCase().includes("heathrow"))
    .slice(0, 2)
    .map((row) => {
      const start = row.selected_journey?.start_time || row.query?.query_time_utc || row.observed_at_utc;
      const arrival = row.selected_journey?.arrival_time || "";
      const duration = row.selected_journey?.total_duration_mins || row.current_duration_mins || row.avg_duration_mins;
      return {
        key: `rail-${row.target?.target_key || row.target_key || row.corridor_key || row.display_name}`,
        label: railLabel(row),
        status: row.status_label || row.target?.effective_status_label,
        detail: formatDuration(duration),
        eta: formatClock(arrival || addMinutes(start, duration)),
      };
    });

  const roadItems = ascotRoads.length ? ascotRoads : roads;
  const railItems = isAscot ? ascotRails : railRows;
  return [...roadItems, ...railItems].filter((item) => item.label && (item.detail || item.eta)).slice(0, 4);
}

const HeathrowLiveStatusCompact = ({
  title = "Live Heathrow checks",
  kicker = "Live Heathrow intelligence",
  airportCode = "LHR",
  airportName = "Heathrow",
  fullStatusPath = "/heathrow/status",
  className = "",
  statusProfile = "",
  footnote = "Roads, flights, terminals, queues, rail and weather are checked before airport journeys.",
}) => {
  const [data, setData] = useState(fallbackSummary);
  const [attractionData, setAttractionData] = useState(null);
  const [railTargetData, setRailTargetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      const summaryStatusUrl = `/api/open/airport-intelligence-summary?airports=${encodeURIComponent(airportCode)}`;
      try {
        const isAscot = String(statusProfile || "").toLowerCase() === "ascot";
        const [rich, attraction, railTargets] = await Promise.all([
          airportCode === "LHR" ? api.get(RICH_STATUS_URL) : api.get(summaryStatusUrl),
          isAscot ? api.get(LEGOLAND_LIVE_URL).catch(() => null) : Promise.resolve(null),
          isAscot
            ? Promise.all(ASCOT_RAIL_TARGET_URLS.map((url) => api.get(url).then((res) => res?.data).catch(() => null)))
            : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setData(airportCode === "LHR" ? (rich?.data || fallbackSummary) : summaryToRichShape(rich?.data, airportCode, airportName));
        setAttractionData(attraction?.data || null);
        setRailTargetData(safeList(railTargets));
        setFallback(false);
      } catch (richErr) {
        try {
          const isAscot = String(statusProfile || "").toLowerCase() === "ascot";
          const [summary, attraction, railTargets] = await Promise.all([
            api.get(summaryStatusUrl),
            isAscot ? api.get(LEGOLAND_LIVE_URL).catch(() => null) : Promise.resolve(null),
            isAscot
              ? Promise.all(ASCOT_RAIL_TARGET_URLS.map((url) => api.get(url).then((res) => res?.data).catch(() => null)))
              : Promise.resolve([]),
          ]);
          if (cancelled) return;
          setData(summaryToRichShape(summary?.data, airportCode, airportName));
          setAttractionData(attraction?.data || null);
          setRailTargetData(safeList(railTargets));
          setFallback(true);
        } catch (summaryErr) {
          if (!cancelled) {
            setData(fallbackSummary);
            setAttractionData(null);
            setRailTargetData([]);
            setFallback(true);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStatus();
    const timer = window.setInterval(loadStatus, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [airportCode, airportName, statusProfile]);

  const cards = useMemo(() => buildCards(data, { includeQueues: airportCode === "LHR" }), [airportCode, data]);
  const journeys = useMemo(() => buildJourneyImpact(data, { statusProfile, attractionData, railTargetData }), [data, statusProfile, attractionData, railTargetData]);
  const overall = data?.status?.overall || fallbackSummary.status.overall;
  const overallStatus = overall.status || overall.label || "unknown";
  const overallTone = toneKey(overallStatus);
  const generatedAt = data?.freshness?.status_generated_at || data?.generated_at || "";
  const freshness = loading ? "Updating..." : formatAge(generatedAt);
  const summary = fallback
    ? `Showing best available ${airportName} checks while the full status feed reconnects.`
    : cleanText(safeList(overall.reasons)[0] || overall.reason || `Live ${airportName} checks are running.`, `Live ${airportName} checks are running.`, 118);

  return (
    <section className={`heathrow-live-compact ${className}`.trim()} aria-label={`Compact ${airportName} live status`}>
      <div className="heathrow-live-compact__head">
        <div>
          <span className="heathrow-live-compact__kicker"><i /> {kicker}</span>
          <h2>{title}</h2>
          <p>{summary}</p>
        </div>
        <div className="heathrow-live-compact__meta">
          <small>Overall</small>
          <span className={`heathrow-live-compact__pill is-${overallTone}`}>{statusLabel(overallStatus)}</span>
          <em>{freshness}</em>
        </div>
      </div>

      <div className="heathrow-live-compact__grid">
        {cards.map((card) => {
          const tone = toneKey(card.status);
          return (
            <article className={`heathrow-live-compact__card is-${tone}`} key={card.key}>
              <div className="heathrow-live-compact__card-top">
                <b>{card.icon}</b>
                <span className={`heathrow-live-compact__pill is-${tone}`}>{statusLabel(card.status)}</span>
              </div>
              <small>{card.label}</small>
              <strong>{card.headline}</strong>
              <div className="heathrow-live-compact__bar" aria-hidden="true">
                <span style={{ width: `${Math.max(8, Math.min(100, card.score || 0))}%` }} />
              </div>
              {card.contributors.length ? (
                <ul>
                  {card.contributors.map((item, index) => (
                    <li key={`${card.key}-${item.target_key || index}`}>{cleanText(item.summary || item.title, "Live contributor", 62)}</li>
                  ))}
                </ul>
              ) : null}
            </article>
          );
        })}
      </div>

      {journeys.length ? (
        <div className="heathrow-live-compact__impact" aria-label={`Current route impact to ${airportName}`}>
          <div className="heathrow-live-compact__impact-head">
            <span>Route impact now</span>
            <small>Specific corridors can differ from the airport-wide status.</small>
          </div>
          <div className="heathrow-live-compact__journeys">
            {journeys.map((item) => {
              const tone = toneKey(item.status);
              return (
                <div className={`heathrow-live-compact__journey is-${tone}`} key={item.key}>
                  <span>
                    <strong>{item.label}</strong>
                    <small>{[item.detail, item.eta ? `${item.timePrefix || "Reach"} ${item.destinationLabel || airportCode} ${item.eta}` : ""].filter(Boolean).join(" · ")}</small>
                  </span>
                  <em>{statusLabel(item.status)}</em>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="heathrow-live-compact__foot">
        <span>{footnote}</span>
        {fullStatusPath ? <Link to={fullStatusPath}>View full {airportName} status <strong>-&gt;</strong></Link> : null}
      </div>
    </section>
  );
};

export default HeathrowLiveStatusCompact;
